/**
 * Sends postMessage to Makeup iframe to apply product.
 * @param {string} category - Category name of selected product.
 * @param {string} key - Concatenate of brand, product and color names.
 */
export const applyProduct = (category, key) =>
  document.getElementById('makeupView').contentWindow.window.postMessage(
    JSON.stringify({
      applyProduct: {
        category: category,
        key: key,
      },
    }),
    '*',
  );

/**
 * Sends postMessage to Nail iframe to apply product by name.
 * @param {string} product_name - Nailpolish product name.
 * @param {string} [pluginSelector='Nailpolish'] - MakeupView must be sent for templates using Plugin v2.
 */
export const applyNailpolishProduct = (product_name, pluginSelector) =>
  document.getElementById(pluginSelector).contentWindow.window.postMessage(
    JSON.stringify({
      applyProduct: product_name,
    }),
    '*',
  );

/**
 * Sends postMessage to Nail iframe to apply product for Plugin v2.
 * @param {string} category - Category name of selected product.
 * @param {string} key - Concatenate of brand, product and color names.
 * @param {boolean} isNail - To tell Plugin v2 that it is nail product or not.
 * @param {string} [pluginSelector='Nailpolish'] - MakeupView must be sent for templates using Plugin v2.
 */
export const applyNailpolishWithCategoryKey = ({ category, key, isNail, pluginSelector }) =>
  document.getElementById(pluginSelector).contentWindow.window.postMessage(
    JSON.stringify({
      applyProduct: {
        category,
        key,
        isNail,
      },
    }),
    '*',
  );

/**
 * Sends postMessage to Makeup iframe to apply eyeglasses for Plugin v2.
 * @param {string} productCode - Product code of selected eyeglasses unit.
 * @param {number} productId - Unique id of selected product.
 */
const applyEyeglassesWithCode = ({ productCode, productId }) =>
  document.getElementById('makeupView').contentWindow.window.postMessage(
    JSON.stringify({
      applyGlassObjectWithName: {
        productId,
        objectName: productCode,
      },
    }),
    '*',
  );

/**
 * Sends postMessage to Makeup iframe to apply eyeglasses for Plugin v2.
 * @param {string} objectUrl - The url of selected eyeglasses unit.
 * @param {number} productId - Unique id of selected product.
 * @param {string|undefined} hdriUrl - HDRI url to apply as glass variant.
 */
export const applyEyeglassesWithUrl = ({ objectUrl, productId, hdriUrl }) =>
  document.getElementById('makeupView').contentWindow.window.postMessage(
    JSON.stringify({
      applyGlassObjectWithUrl: { objectUrl, productId, hdriUrl },
    }),
    '*',
  );

/**
 * Commands that is necessary to run on initialization, it is set in CMS.
 * Creates a script tag and appends it to DOM after filling within.
 * @param {string} fieldValues - JavaScript selectors, object definitions and callbacks.
 */
export const initialize = (fieldValues) => {
  const script = document.createElement('script');
  script.innerHTML = fieldValues;
  document.getElementsByTagName('head')[0].appendChild(script);
};

const EMPTY_PLUGIN_URL = '-';
const PLUGIN_V2_URLS = [
  'bender.pulpoar.com',
  'staging.plugin.pulpoar.com',
  'd34tq17mvbn8xu.cloudfront.net',
];
/**
 * Javascript manual scripts are not needed in Pluginv2, trying to execute them cause error.
 * Check them here to avoid any error in production until BE fields are implemented.
 * @param {string} sourceUrl - Iframe source url to load Plugin.
 * @returns {boolean} - Whether manual scripts are needed or not.
 */
export const shouldExecuteManualScript = (sourceUrl) => {
  if (!sourceUrl || sourceUrl === EMPTY_PLUGIN_URL) return false;
  return !PLUGIN_V2_URLS.find((pluginUrl) => sourceUrl.includes(pluginUrl));
};

/**
 * Inform the plugin about the parent container if needed, and initialize the makeup plugin.
 * @param {string} userID - UUID to pass into makeup engine for authorization.
 * @param {string} sourceUrl - Iframe source url to load Plugin.
 */
export const initializeMakeupEngine = ({ userID, sourceUrl }) => {
  const engineURL = window.location.href;
  // Inform the plugin only when template engine is put inside an iframe.
  if (window.location != window.parent.location) {
    document.getElementById('makeupView').contentWindow.window.postMessage(
      JSON.stringify({
        externalContainerURL:
          document.referrer ||
          'Template could not find referrer url, it is either a mobile app or development environment.',
      }),
      '*',
    );
  }
  if (engineURL.includes('?uploadPhotoActive=true')) {
    document.getElementById('makeupView').contentWindow.window.postMessage(
      JSON.stringify({
        engineURL: {
          engineURL: engineURL,
        },
      }),
      '*',
    );
  }
  if (!shouldExecuteManualScript(sourceUrl)) return;
  init(userID);
};

/**
 * Inform the plugin about the parent container if needed, and initialize the makeup plugin.
 * @param {string} userID - UUID to pass into makeup engine for authorization.
 * @param {string} sourceUrl - Iframe source url to load Plugin.
 */
export const initializeNailEngine = ({ userID, sourceUrl }) => {
  if (!shouldExecuteManualScript(sourceUrl)) return;
  initNailpolish(userID);
};

/**
 * Custom JS commands to run on the first load.
 * Creates a script tag and appends it to DOM after filling within.
 * @param {string} fieldValues - JavaScript selectors, object definitions and callbacks.
 */
export const injectJs = (fieldValues) => {
  const injectCustomJS = document.createElement('script');
  injectCustomJS.innerHTML = fieldValues;
  document.getElementsByTagName('head')[0].appendChild(injectCustomJS);
};

/**
 * Custom CSS definitions to apply on the first load.
 * Creates a style tag and appends it to DOM after filling within.
 * @param {string} fieldValues - CSS selectors.
 */
export const injectCss = (fieldValues) => {
  const injectCustomCSS = document.createElement('style');
  injectCustomCSS.innerHTML = fieldValues;
  document.getElementsByTagName('head')[0].appendChild(injectCustomCSS);
};
